// inv/src/components/ui/CTA.jsx
import { useTranslation } from 'react-i18next'
import { languagePrefixes } from '../../i18n'
import { Mail } from 'lucide-react'
import { Button } from './button'
import googleLogo from '../../pages/authentication/google-logo.svg'
import { useMemo } from 'react'

const IMAGE_TUNING = {
  side: {
    mobile: 'right',
    desktop: 'right',
  },
  mobile: {
    scale: 1.2,
    translate: { x: '10%', y: '0%' },
    origin: 'center right',
    objectPosition: 'center right',
    maxHeight: 'none',
  },
  desktop: {
    scale: 1.3,
    translate: { x: '10%', y: '0%' },
    origin: 'center right',
    objectPosition: 'center right',
    maxHeight: 'none',
  },
}

const CTA = ({
  lng = 'en',
  customTitle,
  customDescription,
  customButtonText,
  customUrl,
  imageUrl = 'https://crino-cdn.nyc3.cdn.digitaloceanspaces.com/CTA_images/CTA_sign_up.svg',
  imageAlt = 'Illustration',
  imageScale = 2,
  imageMaxHeight,
  backgroundPosition = 'center',
}) => {
  const { i18n } = useTranslation()
  const t = useMemo(() => i18n.getFixedT(lng), [i18n, lng])

  const currentLanguage = (lng || 'en').split('-')[0]
  const prefix = languagePrefixes[currentLanguage] || ''

  const title = customTitle || t('pageFindInvestors.t1')
  const description = customDescription || t('pageFindInvestors.t2')
  const buttonText = customButtonText || t('pageHome.content.t4')
  const href = customUrl || `${prefix}/signup`

  const imgMobileOrder = IMAGE_TUNING.side.mobile === 'right' ? 'order-2' : 'order-1'
  const textMobileOrder = IMAGE_TUNING.side.mobile === 'right' ? 'order-1' : 'order-2'
  const imgDesktopOrder = IMAGE_TUNING.side.desktop === 'right' ? 'md:order-2' : 'md:order-1'
  const textDesktopOrder = IMAGE_TUNING.side.desktop === 'right' ? 'md:order-1' : 'md:order-2'

  const fallbackMaxH = imageMaxHeight || 'none'

  return (
    <section className="w-full">
      <style>{`
        .cta-img {
          /* MOBILE */
          --cta-scale: ${Number(IMAGE_TUNING.mobile.scale) || 1};
          --cta-tx: ${IMAGE_TUNING.mobile.translate?.x ?? '0%'};
          --cta-ty: ${IMAGE_TUNING.mobile.translate?.y ?? '0%'};
          --cta-origin-x: ${IMAGE_TUNING.mobile.origin?.split(' ')[0] ?? 'center'};
          --cta-origin-y: ${IMAGE_TUNING.mobile.origin?.split(' ')[1] ?? 'center'};
          --cta-objpos-x: ${IMAGE_TUNING.mobile.objectPosition?.split(' ')[0] ?? 'center'};
          --cta-objpos-y: ${IMAGE_TUNING.mobile.objectPosition?.split(' ')[1] ?? 'center'};
          --cta-maxh: ${IMAGE_TUNING.mobile.maxHeight !== 'none' ? IMAGE_TUNING.mobile.maxHeight : fallbackMaxH};
        }
        @media (min-width: 768px) {
          .cta-img {
            /* DESKTOP */
            --cta-scale: ${Number(IMAGE_TUNING.desktop.scale) || 1};
            --cta-tx: ${IMAGE_TUNING.desktop.translate?.x ?? '0%'};
            --cta-ty: ${IMAGE_TUNING.desktop.translate?.y ?? '0%'};
            --cta-origin-x: ${IMAGE_TUNING.desktop.origin?.split(' ')[0] ?? 'center'};
            --cta-origin-y: ${IMAGE_TUNING.desktop.origin?.split(' ')[1] ?? 'center'};
            --cta-objpos-x: ${IMAGE_TUNING.desktop.objectPosition?.split(' ')[0] ?? 'center'};
            --cta-objpos-y: ${IMAGE_TUNING.desktop.objectPosition?.split(' ')[1] ?? 'center'};
            --cta-maxh: ${IMAGE_TUNING.desktop.maxHeight !== 'none' ? IMAGE_TUNING.desktop.maxHeight : fallbackMaxH};
          }
        }
      `}</style>

      <div
        className="relative mx-auto w-full max-w-6xl rounded-2xl border border-stone-200 bg-white/70 p-4 sm:p-6 md:p-8"
        style={{ backgroundPosition }}
      >
        <div className="grid items-center gap-6 md:grid-cols-2">
          <div className={`${textMobileOrder} ${textDesktopOrder}`}>
            <div className="rounded-xl bg-white/60 p-4 sm:p-5 [container-type:inline-size]">
              <h2 className="mb-2 text-2xl font-bold tracking-tight text-stone-900 text-center sm:text-left sm:text-3xl">{title}</h2>
              <p className="mb-4 text-center sm:text-left text-stone-700 sm:mb-6">{description}</p>
              <Button
                href={href}
                variant="default"
                size="lg"
                className="
      min-w-full overflow-hidden whitespace-nowrap
      inline-flex items-center
      [font-size:clamp(12px,2.4cqw,16px)]
      [padding-inline:clamp(10px,3cqw,20px)]
      [height:clamp(40px,8cqw,48px)]
      [gap:clamp(6px,2cqw,12px)]
    "
                iconLeft={
                  <>
                    <Mail className="mr-1 h-[1em] w-[1em] flex-none" />
                    <img src={googleLogo} alt="Google" className="h-[1em] w-[1em] flex-none" />
                  </>
                }
              >
                <span className="block truncate">{buttonText}</span>
              </Button>
            </div>
          </div>

          <div className={`${imgMobileOrder} ${imgDesktopOrder} flex w-full justify-end`}>
            <div className="relative w-full">
              <img
                src={imageUrl}
                alt={imageAlt}
                className="cta-img block h-auto w-full object-contain"
                style={{
                  transform: `translate(var(--cta-tx), var(--cta-ty)) scale(var(--cta-scale))`,
                  transformOrigin: `var(--cta-origin-x) var(--cta-origin-y)`,
                  objectPosition: `var(--cta-objpos-x) var(--cta-objpos-y)`,
                  maxHeight: `var(--cta-maxh)`,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default CTA
